import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Socket } from "socket.io-client";
import { useUserContext } from "../hooks/userContext";
import { createRealtimeSocket } from "../services/socket";

type PresenceContextValue = {
  getPresenceStatus: (
    userId: string | null | undefined,
    role: string | null | undefined
  ) => boolean | undefined;
};

const PresenceContext = createContext<PresenceContextValue>({
  getPresenceStatus: () => undefined,
});

const buildPresenceKey = (userId: string, role: string) => `${userId}:${role}`;

const normalizeRole = (role?: string | null) => {
  if (!role) return null;
  const trimmed = role.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (lower === "homeowner") return "Homeowner";
  if (lower === "designer") return "Designer";
  if (lower === "admin" || lower === "administrator") return "Admin";
  return trimmed;
};

export const PresenceProvider = ({ children }: PropsWithChildren) => {
  const { user } = useUserContext();
  const [presenceMap, setPresenceMap] = useState<Map<string, boolean>>(
    () => new Map()
  );
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user?.id || !user?.accountType) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setPresenceMap(new Map());
      return;
    }

    setPresenceMap(new Map());
    const socket = createRealtimeSocket(user.id, user.accountType);
    socketRef.current = socket;
    const heartbeatInterval = window.setInterval(() => {
      socket.emit("presence:heartbeat");
    }, 30000);
    socket.emit("presence:heartbeat");

    const handlePresenceUpdate = (payload: {
      userId?: string;
      role?: string;
      isOnline?: boolean;
    }) => {
      const rawUserId = payload?.userId;
      const role = normalizeRole(payload?.role);
      if (!rawUserId || !role) return;
      const key = buildPresenceKey(String(rawUserId), role);
      const isOnline = Boolean(payload?.isOnline);
      setPresenceMap((prev) => {
        const existing = prev.get(key);
        if (existing === isOnline) return prev;
        const next = new Map(prev);
        next.set(key, isOnline);
        return next;
      });
    };

    socket.on("presence:update", handlePresenceUpdate);

    return () => {
      socket.off("presence:update", handlePresenceUpdate);
      window.clearInterval(heartbeatInterval);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id, user?.accountType]);

  const getPresenceStatus = useCallback(
    (userId: string | null | undefined, role: string | null | undefined) => {
      const normalizedRole = normalizeRole(role);
      if (!userId || !normalizedRole) return undefined;
      const key = buildPresenceKey(String(userId), normalizedRole);
      if (!presenceMap.has(key)) return undefined;
      return presenceMap.get(key);
    },
    [presenceMap]
  );

  const value = useMemo(
    () => ({
      getPresenceStatus,
    }),
    [getPresenceStatus]
  );

  return (
    <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>
  );
};

export const usePresence = () => useContext(PresenceContext);
